import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { makeStyles, styled } from '@material-ui/core/styles'
import ArrowForwardIcon from 'components/material-symbol/icons/ArrowForward'
import { interactiveCardHoverStyles } from 'lib/styles/interactive-card-hover'

const BRAND_NAVY = '#001C57'
const LAUNCH_BLUE = '#0053FF'
const OUTLINE_BUTTON_BORDER = '#C4C7C9'
const OUTLINE_BUTTON_ICON = '#425D69'
const LOGO_MAX_HEIGHT = 72
const LOGO_HEADER_HEIGHT = LOGO_MAX_HEIGHT + 32
const HEADER_IMAGE_HEIGHT = 120

const StyledCard = styled(Card)(({ theme, $interactive }) => ({
  borderRadius: 12,
  border: '1px solid #E0E0E0',
  boxShadow: 'none',
  boxSizing: 'border-box',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
  ...($interactive ? interactiveCardHoverStyles(theme) : {}),
  ...($interactive ? { cursor: 'pointer' } : {}),
}))

const useStyles = makeStyles(theme => ({
  logoHeader: {
    backgroundColor: '#FFFFFF',
    color: BRAND_NAVY,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: LOGO_HEADER_HEIGHT,
    minHeight: LOGO_HEADER_HEIGHT,
    padding: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      height: LOGO_HEADER_HEIGHT,
      minHeight: LOGO_HEADER_HEIGHT,
      padding: theme.spacing(1.5),
    },
  },

  logoHeaderWithImage: {
    height: HEADER_IMAGE_HEIGHT,
    minHeight: HEADER_IMAGE_HEIGHT,
    padding: 0,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  },

  logoHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: LOGO_MAX_HEIGHT,
    maxHeight: LOGO_MAX_HEIGHT,
  },

  brand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(1),
    minHeight: 48,
    width: '100%',
  },

  logoDivider: {
    backgroundColor: '#E0E0E0',
  },

  launchSection: {
    marginTop: 'auto',
    width: '100%',
  },

  launchDivider: {
    backgroundColor: '#E0E0E0',
    marginBottom: theme.spacing(1.5),
  },

  cardDescriptionHub: {
    color: '#425D69',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    flex: 1,
    marginBottom: theme.spacing(2),
  },

  logoIconCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    flexShrink: 0,
    '& .MuiSvgIcon-root': {
      fontSize: 36,
      lineHeight: 1,
    },
  },

  logo: {
    display: 'block',
    maxHeight: LOGO_MAX_HEIGHT,
    height: 'auto',
    width: 'auto',
    maxWidth: '200px',
    margin: '0 auto',
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '80%',
    },
  },

  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    '&$bodyWithHeaderImage': {
      paddingTop: theme.spacing(2),
    },
  },

  bodyWithHeaderImage: {},

  category: {
    color: '#425D69',
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: 1.4,
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1.5),
  },

  cardDescription: {
    color: '#6B7C87',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    flex: 1,
    marginBottom: theme.spacing(3),
  },

  launchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    textDecoration: 'none',
    color: 'inherit',
    borderRadius: 8,
    padding: theme.spacing(0.5, 0.5),
    marginLeft: theme.spacing(-0.5),
    marginRight: theme.spacing(-0.5),
    transition: theme.transitions.create(['background-color'], {
      duration: 200,
    }),
    '&:hover': {
      backgroundColor: '#F3F3F3',
      '& $launchLabel': {
        color: BRAND_NAVY,
      },
      '& $launchButton': {
        backgroundColor: LAUNCH_BLUE,
        color: '#FFFFFF',
        borderColor: LAUNCH_BLUE,
      },
      '& $launchButton .MuiSvgIcon-root': {
        transform: 'translateX(2px)',
      },
    },
  },

  launchRowDisabled: {
    pointerEvents: 'none',
    opacity: 0.5,
  },

  launchLabel: {
    color: BRAND_NAVY,
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
    transition: theme.transitions.create(['color'], { duration: 200 }),
  },

  launchButton: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${OUTLINE_BUTTON_BORDER}`,
    color: OUTLINE_BUTTON_ICON,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'color', 'transform'],
      { duration: 200 },
    ),
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      transition: theme.transitions.create(['transform', 'color'], {
        duration: 200,
      }),
    },
  },
}))

const AppCard = ({
  title,
  category,
  testid,
  buttonText,
  to,
  url,
  logo = { src: '', alt: '', className: '' },
  logoStyle = {},
  logoInCircle = false,
  headerImage,
  headerImageAlt = '',
  headerImagePosition,
  brand,
}) => {
  const classes = useStyles()

  const linkProps = {}

  if (to) {
    linkProps.component = RouterLink
    linkProps.to = to
  } else if (url) {
    linkProps.component = 'a'
    linkProps.href = url
    linkProps.target = '_blank'
    linkProps.rel = 'noopener noreferrer'
  }

  const isDisabled = !to && !url

  const renderLogo = () => {
    if (React.isValidElement(logo)) {
      if (logoInCircle) {
        const iconElement = React.cloneElement(logo, {
          className: logo.props.className,
          fontSize: logoStyle.fontSize || 36,
          style: {
            color: '#001C57',
            ...logo.props.style,
            ...logoStyle,
          },
          opticalSize: logoStyle.opticalSize || 36,
        })

        return <div className={classes.logoIconCircle}>{iconElement}</div>
      }

      return logo
    }

    if (typeof logo === 'object' && logo.src) {
      return (
        <img
          src={logo.src}
          alt={logo.alt || ''}
          className={clsx(classes.logo, logo.className)}
          style={logoStyle}
        />
      )
    }

    return null
  }

  const launchRow = (
    <Box
      className={clsx(classes.launchRow, {
        [classes.launchRowDisabled]: isDisabled,
      })}
      {...(isDisabled ? { component: 'div' } : linkProps)}
    >
      <Typography className={classes.launchLabel} component="span">
        {buttonText}
      </Typography>
      <span className={classes.launchButton} aria-hidden="true">
        <ArrowForwardIcon />
      </span>
    </Box>
  )

  return (
    <StyledCard data-testid={testid} $interactive={!isDisabled}>
      <div
        className={clsx(
          classes.logoHeader,
          headerImage && classes.logoHeaderWithImage,
        )}
        style={
          headerImage
            ? {
              backgroundImage: `url(${headerImage})`,
              backgroundPosition: headerImagePosition,
            }
            : undefined
        }
        {...(headerImage && headerImageAlt
          ? { role: 'img', 'aria-label': headerImageAlt }
          : {})}
      >
        {!headerImage ? (
          <div className={classes.logoHeaderContent}>{renderLogo()}</div>
        ) : null}
      </div>
      {!headerImage ? <Divider className={classes.logoDivider} /> : null}
      <Box
        className={clsx(
          classes.body,
          headerImage && classes.bodyWithHeaderImage,
        )}
      >
        {brand ? <Box className={classes.brand}>{brand}</Box> : null}
        {category ? (
          <Typography className={classes.category} component="p">
            {category}
          </Typography>
        ) : null}
        <Typography
          className={
            headerImage ? classes.cardDescriptionHub : classes.cardDescription
          }
          variant="body1"
          component="p"
        >
          {title}
        </Typography>
        {headerImage ? (
          <Box className={classes.launchSection}>
            <Divider className={classes.launchDivider} />
            {launchRow}
          </Box>
        ) : (
          launchRow
        )}
      </Box>
    </StyledCard>
  )
}

AppCard.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  category: PropTypes.string,
  logo: PropTypes.oneOfType([
    PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
      className: PropTypes.string,
    }),
    PropTypes.element,
  ]),
  logoStyle: PropTypes.object,
  logoInCircle: PropTypes.bool,
  headerImage: PropTypes.string,
  headerImageAlt: PropTypes.string,
  headerImagePosition: PropTypes.string,
  brand: PropTypes.node,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  url: PropTypes.string,
  testid: PropTypes.string,
  buttonText: PropTypes.string.isRequired,
}

export default AppCard
